import { QueryClient, QueryFunction } from "@tanstack/react-query";

const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";

/**
 * Thrown when the API answers 401. A dedicated type so callers can tell
 * "you're signed out" apart from "that request broke", without parsing
 * an error string.
 */
export class UnauthorizedError extends Error {
  readonly status = 401;
  constructor(readonly url: string) {
    super(`401 Unauthorized: ${url}`);
    this.name = "UnauthorizedError";
  }
}

/**
 * Returned in place of data when the API says this sister is signed out.
 * A bare `null` could not be told apart from "this query genuinely has no
 * data", so callers get a sentinel and test it with `isSignedOut`.
 */
export const SIGNED_OUT: unique symbol = Symbol("mnf.query.signedOut");
export type SignedOut = typeof SIGNED_OUT;

export function isSignedOut(value: unknown): value is SignedOut {
  return value === SIGNED_OUT;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Parse a JSON body, refusing anything that is not actually JSON.
 *
 * `res.ok` proves nothing about the body. In production an SPA rewrite
 * swallowed every /api path and answered 200 with text/html, so callers got a
 * raw SyntaxError out of res.json() with no hint of what had happened. Check
 * the content-type first and name both what we received and where from.
 */
async function parseJsonBody<T>(res: Response, url: string): Promise<T> {
  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.toLowerCase().includes("application/json")) {
    throw new Error(
      `Expected JSON from ${url} but the server answered ${res.status} with ` +
        `content-type "${contentType ?? "(none)"}". This usually means the ` +
        `request was served by the SPA fallback instead of the API.`,
    );
  }
  return await res.json();
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(`${API_BASE}${url}`, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnSignedOut" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T | SignedOut> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = `${API_BASE}${queryKey.join("/")}`;
    const res = await fetch(url);

    if (res.status === 401) {
      // Either way the caller can tell signed-out from empty: the sentinel
      // is not null, and the error is not a generic Error.
      if (unauthorizedBehavior === "returnSignedOut") return SIGNED_OUT;
      throw new UnauthorizedError(url);
    }

    await throwIfResNotOk(res);
    return await parseJsonBody(res, url);
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
